'use client';

import { ArrowRight, Bot } from 'lucide-react';

import type { ReactNode } from 'react';

import Link from 'next/link';

import { ClaudeLogoIcon, OpenAILogoIcon, SlackLogoIcon } from '@/components/icons/svgs';

import { APP_ROUTES } from '@/shared/constants/navigation';

import { cn } from '@/lib/utils';

import { INLINE_LINK_CLASS } from '../../constants/link-class';
import {
	SECTION_CARD_CLASS,
	SECTION_CLASS,
	SECTION_HEADING_CLASS,
	SECTION_LEAD_CLASS,
} from '../../constants/section-classes';

type AiCard = { icons: ReactNode; title: string; body: string; href?: string };

const AI_CARDS: AiCard[] = [
	{
		icons: <Bot className="size-6 shrink-0 text-primary" />,
		title: 'In Binary',
		body: 'Ask about your pipeline, screen and shortlist candidates, draft jobs and forms, build reports and schedule interviews.',
	},
	{
		icons: <SlackLogoIcon className="size-6 shrink-0" />,
		title: 'In Slack',
		body: 'The same agent in your team’s channels. Get the top candidates, a funnel update or book an interview without opening Binary.',
	},
	{
		icons: (
			<span className="flex items-center gap-1.5">
				<ClaudeLogoIcon className="size-6 shrink-0" />
				<OpenAILogoIcon className="size-6 shrink-0 text-primary" />
			</span>
		),
		title: 'In Claude and ChatGPT',
		body: 'Connect over MCP and use the same actions from the assistant you already work in.',
		href: APP_ROUTES.MCP,
	},
];

/**
 * "One AI agent, wherever you work": the same agent in Binary, Slack and
 * Claude/ChatGPT (MCP) as three cards. Shared by /free-ats and /ai-ats. Pricing is linked from the free-plan
 * limits FAQ. Client component because the icon module uses styled-jsx; it
 * still renders on the server.
 */
type AiAgentSurfacesSectionProps = {
	/** Link the lead to /ai-ats. Off on /ai-ats itself. */
	showAiAtsLink?: boolean;
};

export const AiAgentSurfacesSection = ({ showAiAtsLink = false }: AiAgentSurfacesSectionProps) => {
	return (
		<section className={SECTION_CLASS}>
			<h2 className={SECTION_HEADING_CLASS}>One AI agent, wherever you work</h2>
			<p className={SECTION_LEAD_CLASS}>
				It screens candidates, searches your database, builds reports, creates jobs and schedules
				interviews. Use it in Binary, in Slack or from your own AI assistant, all on the free plan.
				{showAiAtsLink ? (
					<>
						{' '}
						<Link
							href={APP_ROUTES.AI_ATS}
							className={INLINE_LINK_CLASS}
						>
							What makes an AI ATS different
						</Link>
					</>
				) : null}
			</p>

			<ul className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-4 md:mt-12 md:grid-cols-3">
				{AI_CARDS.map((card) => {
					const body = (
						<>
							{card.icons}
							<h3 className="mt-4 text-lg font-semibold text-primary">{card.title}</h3>
							<p className="mt-1.5 text-sm leading-relaxed text-secondary md:text-[15px]">
								{card.body}
							</p>
							{card.href ? (
								<span className="mt-auto inline-flex items-center gap-1 pt-3 text-sm font-medium text-primary">
									How MCP works
									<ArrowRight className="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
								</span>
							) : null}
						</>
					);

					return (
						<li key={card.title}>
							{card.href ? (
								<Link
									href={card.href}
									className={cn(
										SECTION_CARD_CLASS,
										'group flex h-full flex-col transition-all hover:-translate-y-0.5 hover:shadow-md'
									)}
								>
									{body}
								</Link>
							) : (
								<div className={cn(SECTION_CARD_CLASS, 'flex h-full flex-col')}>{body}</div>
							)}
						</li>
					);
				})}
			</ul>

		</section>
	);
};
